import { API_ENDPOINTS } from '@/constants';
import { useMutation } from '@tanstack/react-query';
import { get, handleResponse, post } from './base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import { idbStorage } from '@/utils/idbStorage';
import { resetState, updateTokens } from '@/stores/authStore';
import { logger } from '../utils/logger';
import { UsernameCheckResponse } from '@/constants/Interfaces/CommonInterfaces';
import type {
  ApiResponse,
  TokensResponse,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  ChangePasswordPayload
} from './types';

type LoginPayload = { username: string; password: string };
export const useLoginMutation = () =>
  useMutation({
    mutationFn: (payload: LoginPayload) => {
      return post(API_ENDPOINTS.AUTH_LOGIN, payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useRefreshTokenMutation = () => {
  return useMutation<ApiResponse<TokensResponse>, Error, void>({
    mutationFn: async () => {
      try {
        // Try to get refresh token from secure storage first
        const persistedState = (await idbStorage.secureGetItem(
          'auth-store'
        )) as {
          state?: { userInfo?: { refresh_token?: string } };
        };

        let refresh_token = persistedState?.state?.userInfo?.refresh_token;

        if (!refresh_token) {
          // Fallback to regular storage for backward compatibility
          const fallbackState = (await idbStorage.getItem('auth-store')) as {
            state?: { userInfo?: { refresh_token?: string } };
          };
          refresh_token = fallbackState?.state?.userInfo?.refresh_token;
        }

        if (!refresh_token) {
          throw new Error('No refresh token available');
        }

        return post(API_ENDPOINTS.AUTH_REFRESH_TOKEN, undefined, {
          headers: {
            refresh_token: refresh_token
          }
        }) as Promise<ApiResponse<TokensResponse>>;
      } catch (error) {
        logger.error('Failed to get refresh token for mutation', {
          error: error instanceof Error ? error.message : error
        });
        throw error;
      }
    },
    onSuccess: (data: ApiResponse<TokensResponse>) => {
      // Update stored tokens after successful refresh
      if (data?.success && data?.data) {
        const { access_token, refresh_token } = data.data;
        if (access_token && refresh_token) {
          updateTokens(access_token, refresh_token);
        } else {
          // No valid tokens in response, logout user
          logger.error(
            'Refresh response contains no valid tokens, logging out user'
          );
          resetState();
          throw new Error('No valid tokens received from refresh');
        }
      } else {
        // Invalid response format, logout user
        logger.error('Invalid refresh response format, logging out user');
        resetState();
        throw new Error('Invalid refresh response');
      }
    },
    onError: (error: unknown) => {
      logger.error('Refresh token mutation failed', {
        error: error instanceof Error ? error.message : error
      });
      // Logout user on any refresh error
      resetState();
      appHandleError(error);
    }
  });
};

export const useLogoutMutation = () => {
  return useMutation({
    mutationFn: async () => {
      try {
        // Try to get tokens from secure storage first
        const persistedState = (await idbStorage.secureGetItem(
          'auth-store'
        )) as {
          state?: { userInfo?: { token?: string; refresh_token?: string } };
        };

        let token = persistedState?.state?.userInfo?.token;
        let refresh_token = persistedState?.state?.userInfo?.refresh_token;

        if (!token || !refresh_token) {
          // Fallback to regular storage for backward compatibility
          const fallbackState = (await idbStorage.getItem('auth-store')) as {
            state?: { userInfo?: { token?: string; refresh_token?: string } };
          };
          token = token || fallbackState?.state?.userInfo?.token;
          refresh_token =
            refresh_token || fallbackState?.state?.userInfo?.refresh_token;
        }

        // If we have an access token, call logout API
        if (token) {
          const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`
          };

          if (refresh_token) {
            headers['refresh_token'] = refresh_token;
          }

          return post(
            API_ENDPOINTS.AUTH_LOGOUT,
            {},
            {
              method: 'POST',
              headers,
              isLogoutRequest: true // Flag to prevent token refresh on 401
            }
          );
        }

        // If no access token, just clear local state
        return Promise.resolve({
          success: true,
          message: 'Logged out successfully'
        });
      } catch (error) {
        logger.error('Failed to get tokens for logout', {
          error: error instanceof Error ? error.message : error
        });
        // Even if we can't get tokens, proceed with logout
        return Promise.resolve({
          success: true,
          message: 'Logged out successfully'
        });
      }
    },
    onSuccess: response => {
      // Always clear local state regardless of API response
      resetState();
      idbStorage.removeItem('auth-store');
      handleResponse(response);
    },
    onError: error => {
      // Even if logout API fails, clear local state
      resetState();
      idbStorage.removeItem('auth-store');
      // Convert error to ApiResponse format for handleError
      const apiError = {
        success: false,
        message: error instanceof Error ? error.message : 'Logout failed',
        status: 500
      };
      appHandleError(apiError);
    }
  });
};

export const useForgotPasswordMutation = () =>
  useMutation<ApiResponse<unknown>, Error, ForgotPasswordPayload>({
    mutationFn: (payload: ForgotPasswordPayload) => {
      return post(
        API_ENDPOINTS.AUTH_FORGOT_PASSWORD,
        payload as unknown as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useResetPasswordMutation = () =>
  useMutation<ApiResponse<unknown>, Error, ResetPasswordPayload>({
    mutationFn: (payload: ResetPasswordPayload) => {
      return post(
        API_ENDPOINTS.AUTH_RESET_PASSWORD,
        payload as unknown as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useChangePasswordMutation = () =>
  useMutation<ApiResponse<unknown>, Error, ChangePasswordPayload>({
    mutationFn: (payload: ChangePasswordPayload) => {
      return post(
        API_ENDPOINTS.AUTH_CHANGE_PASSWORD,
        payload as unknown as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useCheckUsernameMutation = () =>
  useMutation<UsernameCheckResponse, unknown, string>({
    mutationFn: (payload: string) => {
      return get(
        `${API_ENDPOINTS.AUTH_CHECK_USERNAME}?${payload}`
      ) as Promise<UsernameCheckResponse>;
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });
